import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StageFilterBar } from '../../components/logs/StageFilterBar';
import { StageSummaryCard } from '../../components/logs/StageSummaryCard';
import { LogTable } from '../../components/logs/LogTable';
import { useFetchMigrationLogs } from '../../hooks/api/useFetchMigrationLogs';
import type { Migration } from '../../types';
import type { MigrationStage, LogLevel, MigrationLogEntry } from '../../types/logs';
import type { MigrationLogsResponse } from '../../types/logs';
import { mockFetchMigrationLogs } from '../../utils/mockLogData';
import { ui } from '../../config/uiSizes';
import { logger } from '../../utils/logger';

// Set to true to use mock data for prototyping (no backend required)
const USE_MOCK_DATA = false;

/**
 * Main page for viewing migration logs
 * Accessible from dashboard view modal or approve pages
 */
export const MigrationLogsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedJob = location.state?.job as Migration | undefined;

  // Local filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevelFilter, setSelectedLevelFilter] = useState<'all' | LogLevel>('all');
  const [mockLogs, setMockLogs] = useState<MigrationLogsResponse | null>(null);
  const [mockLoading, setMockLoading] = useState(false);

  // Fetch logs with hook (or use mock data)
  const {
    logs: realLogs,
    totalCount: realTotalCount,
    migrationName: realMigrationName,
    loading: realLoading,
    error: realError,
    filters,
    updateFilters
  } = useFetchMigrationLogs({
    migrationId: selectedJob?.id || '',
    initialFilters: {
      stages: [],
      levels: [],
      searchTerm: '',
      page: 1,
      pageSize: 10
    },
    autoFetch: !USE_MOCK_DATA && !!selectedJob?.id
  });

  // Log page mount for traceability
  useEffect(() => {
    logger.info('Page: MigrationLogs mounted', { migrationId: selectedJob?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load mock data if enabled
  useEffect(() => {
    if (USE_MOCK_DATA && selectedJob?.id) {
      setMockLoading(true);
      // Determine which stages to include based on migration status
      const includeStages: ('Export' | 'Transform' | 'Import')[] = [];
      const status = String(selectedJob.status).toLowerCase();

      if (
        status.includes('export') ||
        status.includes('transform') ||
        status.includes('import') ||
        status.includes('complete')
      ) {
        includeStages.push('Export');
      }
      if (
        status.includes('transform') ||
        status.includes('import') ||
        status.includes('complete')
      ) {
        includeStages.push('Transform');
      }
      if (status.includes('import') || status.includes('complete')) {
        includeStages.push('Import');
      }

      // Default to Export and Transform if we can't determine from status
      const stages: ('Export' | 'Transform' | 'Import')[] =
        includeStages.length > 0 ? includeStages : ['Export', 'Transform'];

      mockFetchMigrationLogs(selectedJob.id, selectedJob.name, stages)
        .then(setMockLogs)
        .finally(() => setMockLoading(false));
    }
  }, [selectedJob?.id, selectedJob?.name, selectedJob?.status]);

  // Use mock or real data
  const logs = useMemo(
    () => (USE_MOCK_DATA ? mockLogs?.logs || [] : realLogs),
    [mockLogs?.logs, realLogs]
  );
  const totalCount = USE_MOCK_DATA ? mockLogs?.totalCount || 0 : realTotalCount;
  const migrationName = USE_MOCK_DATA
    ? mockLogs?.migrationName || selectedJob?.name
    : realMigrationName;
  const loading = USE_MOCK_DATA ? mockLoading : realLoading;
  const error = USE_MOCK_DATA ? null : realError;

  // Calculate which stages have logs available
  const availableStages = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    const stages = new Set<MigrationStage>();
    logs.forEach((log: MigrationLogEntry) => stages.add(log.stage));
    return Array.from(stages);
  }, [logs]);

  // Handle stage filter changes
  const handleStageChange = (stages: MigrationStage[]) => {
    updateFilters({ stages, page: 1 });
  };

  // Handle level filter change
  const handleLevelFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'all' | LogLevel;
    setSelectedLevelFilter(value);

    const levels: LogLevel[] = value === 'all' ? [] : [value];
    updateFilters({ levels, page: 1 });
  };

  // Handle search with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Simple debounce - in production, use a proper debounce utility
    const timeoutId = setTimeout(() => {
      updateFilters({ searchTerm: value, page: 1 });
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Pagination controls
  const totalPages = Math.ceil(totalCount / filters.pageSize);
  const handlePageChange = (newPage: number) => {
    updateFilters({ page: newPage });
  };

  // Redirect if no job provided
  if (!selectedJob) {
    return (
      <div className={`${ui.page.padding}`}>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No migration selected.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${ui.page.padding}`}>
      <div className="flex flex-col flex-1 rounded-2xl border border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mx-2 mt-0 mb-2 p-2">
        {/* Page header */}
        <div
          className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 ${ui.page.cardPadding} ${ui.mb.title}`}
        >
          <div className="flex flex-col gap-0.5">
            <span
              className={`${ui.text.xs} font-bold tracking-widest uppercase text-blue-600 dark:text-blue-400`}
            >
              {selectedJob.isSnapshot ? 'Snapshot Logs' : 'Migration Logs'}
            </span>
            <h2
              className={`${ui.text.pageTitle} font-extrabold text-gray-900 dark:text-gray-100 tracking-tight`}
            >
              {migrationName || selectedJob.name}
            </h2>
          </div>
        </div>

        {/* Main content */}
        <div
          className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 ${ui.page.cardPadding}`}
        >
          {/* Filters section */}
          <div className={`space-y-4 ${ui.mb.section}`}>
            {/* Stage filters - only show for migrations, not snapshots */}
            {!selectedJob.isSnapshot && (
              <StageFilterBar
                selectedStages={filters.stages}
                onStageChange={handleStageChange}
                availableStages={availableStages}
              />
            )}

            {/* Search and level filter row */}
            <div className={`flex flex-col sm:flex-row ${ui.gap.lg}`}>
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              <div className="sm:w-48">
                <select
                  value={selectedLevelFilter}
                  onChange={handleLevelFilterChange}
                  className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="all">All Levels</option>
                  <option value="Info">Info Only</option>
                  <option value="Success">Success Only</option>
                  <option value="Warning">Warnings Only</option>
                  <option value="Error">Errors Only</option>
                </select>
              </div>
            </div>
          </div>
          {/* Stage summary cards - show when stages are selected (migrations only) */}
          {!selectedJob.isSnapshot && filters.stages.length > 0 && (
            <div className={`space-y-3 ${ui.mb.section}`}>
              {filters.stages.map((stage) => (
                <StageSummaryCard key={stage} stage={stage} logs={logs} />
              ))}
            </div>
          )}
          {/* Error display */}
          {error && (
            <div
              className={`${ui.mb.title} ${ui.page.sectionPadding} bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg`}
            >
              <p className={`${ui.text.label} text-red-800 dark:text-red-200`}>{error}</p>
            </div>
          )}

          {/* Log table */}
          <LogTable logs={logs} loading={loading} />

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className={`${ui.text.pagination} text-gray-700 dark:text-gray-300`}>
                  Page {filters.page} of {totalPages}
                </span>
                <span className={`${ui.text.pagination} text-gray-500 dark:text-gray-400`}>
                  ({totalCount} total logs)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={filters.page === 1}
                  className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${ui.text.pagination}`}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1}
                  className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${ui.text.pagination}`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (filters.page <= 3) {
                      pageNum = i + 1;
                    } else if (filters.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = filters.page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`${ui.button.pagination} rounded-lg font-semibold transition ${filters.page === pageNum
                          ? 'bg-blue-600 text-white dark:bg-blue-700'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= totalPages}
                  className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600`}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={filters.page >= totalPages}
                  className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${ui.text.pagination}`}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
